import { styled } from "@linaria/react";
import React from "react";
import type { ActiveAnalysis } from "../../../../../../../../../../api/useActiveAnalysesStream";
import type { IndexedFailureValues } from "../../../../../../../../../../api/useIndexedFailureValues";
import type { Project } from "../../../../../../../../../../api/useProjects";
import type { Quantity } from "../../../../../../../../../../api/useQuantities";
import type { Setup } from "../../../../../../../../../../api/useSetups";
import HighchartsComponent from "@ui/Highcharts";
import { getAnalysisSetup } from "diagnostics-domain/setup/getSetup";
import type { IndexedManualFailureValues } from "../../../../../../../../../../api/useManualFailureValues";
import type { IndexedFailureMetadata } from "../../../../../../../../../../api/useFailureMetadata";
import type { ObservableOrder } from "../../../../../../../../Diagnosis/Symptoms/Observables/useOptions";
import useSeries from "../../../../../../../../Diagnosis/Symptoms/useSeries";
import { useDisplayAllQuantities } from "../../../../../../../hooks/createGlobalStates/useDisplayAllQuantities";
import { filterSeriesByEventOccurrence, filterSeriesByFocus } from "../../isFocusedFaultSeries";
import { useDisplayAllFaults } from "../../useDisplayAllFaults";
import useOptions from "../../../../../../../../Diagnosis/Symptoms/Observables/useOptions";
import { QuantitySelectorType } from "../../../../../../../../QuantitySelector";
import { useRevelantQuantities } from "../../../../../../../hooks/useRevelantQuantities";
import { getEventFaultNames, getEventScope } from "../../../../../../../../../../domain/event";
import type { Event } from "../../../../../../../../../../api/useEvents";
import { getEventVisibleImpactSeries } from "../../../../../../../utils/event";

interface P {
  visibleSymptomHistogramChartId: string;
  event: Event;
  project: Project;
  setups: Setup[];
  analysis: ActiveAnalysis;
  indexedFailureMetadata: IndexedFailureMetadata;
  indexedFailureValues: IndexedFailureValues;
  indexedManualFailureValues: IndexedManualFailureValues;
  order: ObservableOrder;
  observableChartRef: React.RefObject<Highcharts.Chart>;
  quantities: Quantity[];
  showInactiveObservables: boolean;
}

const FixedWrapper = styled.div`
  position: relative;
  overflow: hidden;
`;

const Wrapper = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  height: initial !important; /* remove Higcharts style override... */
`;

const ObservablesChart = ({
  visibleSymptomHistogramChartId,
  event,
  project,
  setups,
  analysis,
  indexedFailureMetadata,
  indexedFailureValues,
  indexedManualFailureValues,
  order,
  observableChartRef,
  quantities,
  showInactiveObservables,
}: P) => {
  const eventScope = getEventScope(event);
  const [displayAllFaults, _] = useDisplayAllFaults({
    scope: eventScope,
  });
  const [displayAllQuantities] = useDisplayAllQuantities({
    scope: eventScope,
  });
  const revelantQuantities = useRevelantQuantities({
    dataType: QuantitySelectorType.Diagnosis,
    projectId: project.id,
    event,
    setups,
    quantities,
  });

  const setup = getAnalysisSetup(setups, analysis);
  const inputs = React.useMemo(() => (setup ? setup.diagnosisInputs : []), [setup]);
  const observables = React.useMemo(() => {
    const obsQuantities = inputs
      .filter(input =>
        showInactiveObservables
          ? quantities.some(quantity => quantity.id === input.quantityId)
          : quantities.some(quantity => quantity.id === input.quantityId) && input.enabled,
      )
      .flatMap(input => {
        const quantityFound = quantities.find(q => input.quantityId === q.id);
        if (quantityFound) {
          return [
            {
              ...input,
              ...quantityFound,
            },
          ];
        }

        return [];
      });

    const filteredObservables =
      event.type === "MANUAL" || displayAllQuantities
        ? obsQuantities
        : obsQuantities.filter(obs => {
            const isRelevant = revelantQuantities.some(r => r.id === obs.id);
            return isRelevant;
          });

    return filteredObservables;
  }, [
    inputs,
    quantities,
    showInactiveObservables,
    displayAllQuantities,
    revelantQuantities,
    event.type,
  ]);

  const observableSeries = useSeries(
    project,
    analysis,
    indexedFailureMetadata,
    indexedFailureValues,
    indexedManualFailureValues,
    setup,
    observables,
    true,
  );

  const failureNames = getEventFaultNames(event);
  const series = React.useMemo(() => {
    const focusedSeries = filterSeriesByFocus({
      series: observableSeries,
      event,
      displayAllFaults,
      failureNames,
      getEventVisibleSeries: getEventVisibleImpactSeries,
    });
    return filterSeriesByEventOccurrence({
      series: focusedSeries,
      event,
      date: analysis.date,
      displayAllFaults,
    });
  }, [displayAllFaults, failureNames, observableSeries, event, analysis.date]);

  const options = useOptions({
    visibleSymptomHistogramChartId,
    series,
    analysis,
    order,
    quantities,
    setups,
    project,
    showInactiveObservables,
    flagSelectedQuantities: true,
    event,
    observables,
  });
  return (
    <FixedWrapper>
      <Wrapper>
        <HighchartsComponent
          ref={observableChartRef}
          name="Diagnosis impacts"
          options={options}
          selectablePoints={false}
        />
      </Wrapper>
    </FixedWrapper>
  );
};

export default React.memo(ObservablesChart);
